'use client';

import { useState, type ReactNode } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Badge } from '../../../../components/badge';
import { Button } from '../../../../components/button';
import { Collapsible } from '../../../../components/collapsible';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Globe02Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Text } from '../../../../components/styled/text';
import { HttpArtifact } from '../../../artifacts/http/HttpArtifact';
import { useToolCallHarAvailability } from './useToolCallHarAvailability';

type ToolCallHarAvailability = ReturnType<typeof useToolCallHarAvailability>;

type PythonToolHarSectionProps = {
  toolCallId: string;
  toolName: string;
  fallback?: ReactNode;
  defaultExpanded?: boolean;
  refreshKey?: string;
  presentation?: 'collapsible' | 'inline';
  waitForHar?: boolean;
  harAvailability?: ToolCallHarAvailability;
};

export function PythonToolHarSection({
  toolCallId,
  toolName,
  fallback,
  defaultExpanded,
  refreshKey,
  presentation = 'collapsible',
  waitForHar = false,
  harAvailability: harAvailabilityFromParent,
}: PythonToolHarSectionProps) {
  const t = useTranslations('designSystem.chat');
  const [isExpanded, setIsExpanded] = useState(Boolean(defaultExpanded));
  const shouldUseParentHarAvailability =
    harAvailabilityFromParent !== undefined &&
    (harAvailabilityFromParent.isLoading ||
      harAvailabilityFromParent.harCount !== null);
  const internalHarAvailability = useToolCallHarAvailability({
    toolCallId,
    refreshKey,
    enabled: !shouldUseParentHarAvailability,
  });
  const { harCount, isLoading, harIsPresent } = shouldUseParentHarAvailability
    ? harAvailabilityFromParent
    : internalHarAvailability;
  const isInline = presentation === 'inline';
  const toolType =
    toolName === 'python' ||
    toolName === 'python-tool' ||
    toolName === 'python_tool'
      ? 'PYTHON'
      : 'HTTP';

  if (isLoading || harCount === null) {
    return isInline ? (fallback ?? null) : null;
  }

  if (harCount === 0 && !(waitForHar && harIsPresent)) {
    return fallback ?? null;
  }

  const harArtifact = (
    <HttpArtifact
      toolCallId={toolCallId}
      toolType={toolType}
      showSummaryHeader
    />
  );

  if (isInline) {
    return (
      <Box width="100%" bg="transparent" borderRadius="md">
        {harArtifact}
      </Box>
    );
  }

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
    >
      <Box borderTopWidth="1px" borderColor="border.subtle" pt={2} mt={2}>
        <Collapsible.Trigger asChild>
          <Button
            variant="ghost"
            size="sm"
            width="full"
            justifyContent="flex-start"
          >
            <HStack gap={2} alignItems="center" w="full">
              <Globe02Icon size={14} />
              <Text fontSize="sm" fontWeight="medium">
                {t('httpRequests')}
              </Text>
              <Badge size="sm" variant="emphasis">
                {harCount}
              </Badge>
              <Box ml="auto" display="flex" alignItems="center">
                {isExpanded ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
              </Box>
            </HStack>
          </Button>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Box p={3} bg="transparent" borderRadius="md" mt={2}>
            {harArtifact}
          </Box>
        </Collapsible.Content>
      </Box>
    </Collapsible.Root>
  );
}
